import {
  EnquiryHandle,
  IServerSideGetRowsParams,
  ServerSideCriteria,
  useServerEnquiry,
} from '@access-erp/common';
import { CustomDetailsProps } from '@access-erp/ui-components';
import { Guid } from 'guid-typescript';
import { useEffect, useRef, useState } from 'react';

import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import {
  Forms,
  BundleOutput,
  BundleSort,
  BundleSortSortByInput,
} from '@/graphql/contracts/_generated_/contracts';
import {
  GetPaginatedFilteredBundlesVariables,
} from '@/graphql/contracts/_generated_/getPaginatedFilteredBundles.query';

import { getDetailColumns } from './columns/detailColumns';
import { useColumns } from './hooks/useColumns';
import useGetBundles from './hooks/useGetBundles';
import useGetDetails from './hooks/useGetDetails';
import { useRightPanel } from './hooks/useRightPanel';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { UserRole } from '@/config/roles.config';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { replaceAdvancedValueType } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';

const BundlesComponent = () => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole>();
  const { getUserRole } = useGetUserRole();
  const gridRef = useRef<EnquiryHandle<BundleOutput>>(null);
  const { headerColumns, goToBundlesBySearch } = useColumns() as any;
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();

  const { getBundles } = useGetBundles();

  const detailCols = getDetailColumns() as any;

  const getDetails = useGetDetails();
  const enquiry = useServerEnquiry<BundleOutput, GetPaginatedFilteredBundlesVariables['filter']>({
    gridRef: gridRef,
    headerColumns,
    detailColumns: detailCols,
    initialFilter: replaceAdvancedValueType(savedFilters),
  });

  const { tabsConfiguration } = useRightPanel({
    selectedHeaderRows: enquiry.selectedHeaderRows,
    gridRef: gridRef.current!,
  });

  useEffect(() => {
    const getInitialData = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getInitialData();
  }, [getUserRole]);

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  const getData = async (
    params: IServerSideGetRowsParams<BundleOutput>,
    serverSideCriteria: ServerSideCriteria<BundleSort, BundleSortSortByInput>,
  ) => {
    await getSavedFilterIfAny(Forms.Bundles, params);

    const bundleData = await getBundles({
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: serverSideCriteria.userSetSorting as any,
      group: serverSideCriteria.groupByColumn as any,
    });

    setPageIsLoading(false);

    const searchIsCleared = enquiry.serverSide?.current?.search === '';

    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToBundlesBySearch(enquiry.serverSide?.current?.search);
    }

    const bundles =
      bundleData?.items?.map((el) => ({
        ...el,
        id: el?.id && !Guid.parse(el.id).isEmpty() ? el.id : Guid.create().toString(),
      })) ?? [];

    return {
      items: bundles,
      pageInfo: {
        hasNextPage: bundleData?.pageInfo?.hasNextPage ?? false,
        hasPreviousPage: bundleData?.pageInfo?.hasPreviousPage ?? false,
        endCursor: bundleData?.pageInfo?.endCursor ?? null,
        startCursor: bundleData?.pageInfo?.startCursor ?? null,
      },
    };
  };

  const customDetailsProps: CustomDetailsProps<{ id: string | number }> = {
    getData: getDetails,
    primaryHeaderKey: 'id',
    filtersRef: enquiry.filtersRef,
    detailRowSelection: 'none',
    applyRowMasterFlag: true,
  } as unknown as CustomDetailsProps<{ id: string | number }>;

  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef as any}
        enquiry={enquiry as any}
        filterForm={Forms.Bundles}
        formName={'Bundles'}
        headerProps={{
          primaryTitle: 'Bundles',
          secondaryTitle: 'List',
          searchPlaceholder: 'Start typing Description or Contract Number',
          search: searchTextFromQuery,
          showExportAll: userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN,
          showExportCurrent: false,
        }}
        detailProps={{
          exportFileName: 'Bundles',
          rowGroupPanelShow: 'always',
          customDetailsProps,
          masterDetail: true,
          getData: getData as any,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          loading: pageIsLoading,
        }}
        rightPanelTabs={tabsConfiguration}
      />
    </>
  );
};

export default BundlesComponent;
